import {
  ArchiveBoxIcon,
  DocumentIcon,
  DocumentTextIcon,
  PhotoIcon,
  TableCellsIcon,
} from "@heroicons/react/20/solid";
import {ComponentType, SVGProps} from "react";

export type OtsFileKind = "image" | "pdf" | "document" | "spreadsheet" | "archive" | "other";

const extensions: Record<string, OtsFileKind> = {
  jpg: "image", jpeg: "image", png: "image", gif: "image", webp: "image", heic: "image", bmp: "image", svg: "image",
  pdf: "pdf",
  doc: "document", docx: "document", txt: "document", rtf: "document",
  xls: "spreadsheet", xlsx: "spreadsheet", csv: "spreadsheet",
  zip: "archive", gz: "archive", tar: "archive",
};

/** What kind of file something is, from its content type when known and otherwise its extension. */
export const getFileKind = (fileName?: string | null, contentType?: string | null): OtsFileKind => {
  const type = contentType?.toLowerCase() ?? "";
  if (type.startsWith("image/")) return "image";
  if (type === "application/pdf") return "pdf";
  if (type.includes("spreadsheet") || type.includes("excel") || type === "text/csv") return "spreadsheet";
  if (type.includes("word") || type.startsWith("text/")) return "document";
  if (type.includes("zip")) return "archive";
  const extension = fileName?.split(/[?#]/)[0].split(".").pop()?.toLowerCase();
  return (extension && extensions[extension]) || "other";
};

const kinds: Record<OtsFileKind, {Icon: ComponentType<SVGProps<SVGSVGElement>>; color: string; label: string}> = {
  image: {Icon: PhotoIcon, color: "text-[var(--ots-blue)]", label: "Picture"},
  pdf: {Icon: DocumentTextIcon, color: "text-[var(--ots-error)]", label: "PDF"},
  document: {Icon: DocumentTextIcon, color: "text-[var(--ots-indigo)]", label: "Document"},
  spreadsheet: {Icon: TableCellsIcon, color: "text-[var(--ots-success)]", label: "Spreadsheet"},
  archive: {Icon: ArchiveBoxIcon, color: "text-[var(--ots-warning)]", label: "Archive"},
  other: {Icon: DocumentIcon, color: "text-[var(--ots-text-muted)]", label: "File"},
};

export const otsFileKindLabel = (kind: OtsFileKind): string => kinds[kind].label;

export type OtsFileTypeIconProps = {
  kind: OtsFileKind;
  /** Names the icon for assistive technology; decorative without it. */
  label?: string;
  className?: string;
};

/** One icon and color per kind of file, so a list of attachments can be read at a glance. */
export const OtsFileTypeIcon = ({kind, label, className}: OtsFileTypeIconProps) => {
  const {Icon, color} = kinds[kind];
  return <Icon
    role={label ? "img" : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : true}
    data-file-kind={kind}
    className={[color, className ?? "size-4"].join(" ")}/>;
};
